import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import SurveyQuestionProp from './propTypes/surveyQuestionProp';
import { SURVEY_QUESTION_TYPE } from '../../services/utilityFunctions';
import SingleChoice from './questions/SingleChoice';
import MultipleChoice from './questions/MultipleChoice';
import Text from './questions/Text';
import Evaluation from './questions/Evaluation';
import SkipStepButton from '../journey/overall/content/components/SkipStepButton';

export default function SurveyDisplay({ questions, onSurveyDone, onSkipStep }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [localQuestions, setLocalQuestions] = useState(questions);

  useEffect(() => setLocalQuestions(questions), [questions]);
  useEffect(() => {
    const nextUnanswered = localQuestions.findIndex(q => !q.answer);
    if (nextUnanswered !== -1) {
      setSelectedIndex(nextUnanswered);
    } else {
      onSurveyDone(
        localQuestions.map(({ _id, answer }) => ({
          question: _id,
          ...answer,
        })),
      );
    }
  }, [localQuestions]);

  const indicatorClass = index =>
    cx('quiz-question-indicator', {
      'status-correct': !!localQuestions[index].answer,
    });

  const {
    _id: questionId,
    question,
    hint,
    evaluationMax,
    choices,
    options,
    type,
    image,
    answer,
  } = localQuestions[selectedIndex];

  const handleSubmit = selectedAnswer => {
    const updatedQuestions = localQuestions.map(q => {
      if (q._id === questionId) {
        return { ...q, answer: selectedAnswer };
      }
      return q;
    });
    setLocalQuestions(updatedQuestions);
  };

  const { Component, optionConfig } = (() => {
    switch (type) {
      case SURVEY_QUESTION_TYPE.OPTIONS:
        return {
          Component: SingleChoice,
          optionConfig: options,
        };
      case SURVEY_QUESTION_TYPE.CHOICE:
        return {
          Component: SingleChoice,
          optionConfig: Object.entries(choices).map(([_id, option]) => ({
            _id,
            option,
          })),
        };
      case SURVEY_QUESTION_TYPE.MULTIPLE_ANSWERS:
        return {
          Component: MultipleChoice,
          optionConfig: options,
        };
      case SURVEY_QUESTION_TYPE.EVALUATION:
        return {
          Component: Evaluation,
          optionConfig: evaluationMax,
        };
      case SURVEY_QUESTION_TYPE.OPINION:
      default:
        return { Component: Text };
    }
  })();

  return (
    <div className="course-quiz-wrapper">
      <div className="m-portlet m-portlet--rounded">
        <div className="m-portlet__body">
          {onSkipStep && (
            <div className="skip-wrapper m-0">
              <SkipStepButton onClick={onSkipStep} />
            </div>
          )}
          <div className="quiz-question-container">
            <div className="quiz-question-question">
              {question}
              {image && (
                <img
                  alt="question description"
                  className="question-image"
                  src={image.url}
                />
              )}
              {hint && <span className="question-hint">{hint}</span>}
            </div>
            <div className="quiz-question-indicator-container">
              {localQuestions.map((q, index) => (
                <div
                  style={{ cursor: 'pointer' }}
                  key={q._id}
                  className={indicatorClass(index)}
                  onClick={() => setSelectedIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="m-portlet m-portlet--rounded">
        <Component
          questionId={questionId}
          value={answer}
          options={optionConfig}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
}

SurveyDisplay.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.shape(SurveyQuestionProp.propType))
    .isRequired,
  onSurveyDone: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func,
};

SurveyDisplay.defaultProps = {
  onSkipStep: null,
};
